import React, { useEffect, useState, useMemo } from 'react';
import { Container, Button, Form, Spinner, ListGroup, Badge, Row, Col, Card } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../../firebase';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../../../components/context/AuthContext';
import Layout from '../../../components/ui/Layout';
import { useNotification } from '../../../components/ui/Notification';
import { createLogger } from '../../../utils/logger';
import {
  DEFAULT_PRICE_TAX_MODE,
  DEFAULT_TAX_CATEGORY,
  TAX_CATEGORIES,
  getDefaultTaxRateForCategory,
} from '../../../constants/jamaicaGct';

const logger = createLogger('EditInventoryItem');

const EditInventoryItem = () => {
  const { itemId } = useParams();
  const { currentUser, userRole, employeeSupermarketId } = useAuth();
  const { showNotification } = useNotification();
  const [supermarkets, setSupermarkets] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedSupermarketId, setSelectedSupermarketId] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [taxCategory, setTaxCategory] = useState(DEFAULT_TAX_CATEGORY);
  const [customTaxRate, setCustomTaxRate] = useState('');
  const [quantity, setQuantity] = useState('');
  const [pageLoading, setPageLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProductData, setSelectedProductData] = useState(null);

  // Discount deals state
  const [discountDeals, setDiscountDeals] = useState([]);
  const [dealQuantity, setDealQuantity] = useState('');
  const [dealPrice, setDealPrice] = useState('');

  const navigate = useNavigate();

  const isEmployeeLimited = userRole === 'Employee' && employeeSupermarketId;
  const canEdit = userRole === 'Admin' || (isEmployeeLimited && selectedSupermarketId === employeeSupermarketId);
  const selectedSupermarket = useMemo(
    () => supermarkets.find((supermarket) => supermarket.id === selectedSupermarketId),
    [supermarkets, selectedSupermarketId]
  );
  const selectedPriceTaxMode = selectedSupermarket?.price_tax_mode || DEFAULT_PRICE_TAX_MODE;
  const isTaxInclusivePricing = selectedPriceTaxMode === DEFAULT_PRICE_TAX_MODE;

  // Filter and paginate products based on search term
  const filteredProducts = useMemo(() => {
    let filteredList = products;

    if (productSearchTerm.trim()) {
      const rawTerm = productSearchTerm.trim();
      const termLower = rawTerm.toLowerCase();
      filteredList = products.filter(product =>
        product.display_name.toLowerCase().includes(termLower) ||
        (product.upc && product.upc.includes(rawTerm)) ||
        (product.description && product.description.toLowerCase().includes(termLower))
      );
    }

    return filteredList.slice(0, 15);
  }, [products, productSearchTerm]);

  const handleProductSelect = (product) => {
    setSelectedProductData(product);
    setSelectedProduct(product.id);
    setProductSearchTerm(product.display_name);
    setShowProductDropdown(false);
  };

  const handleProductSearchChange = (e) => {
    const value = e.target.value;
    setProductSearchTerm(value);
    setShowProductDropdown(true);
    if (!value.trim()) {
      setSelectedProductData(null);
      setSelectedProduct('');
    }
  };

  // Discount deals handlers
  const addDiscountDeal = () => {
    if (!dealQuantity || !dealPrice) {
      showNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Please enter both quantity and price for the discount deal.',
        duration: 5000
      });
      return;
    }

    const quantityNum = parseInt(dealQuantity);
    const priceNum = parseFloat(dealPrice);

    if (isNaN(quantityNum) || quantityNum <= 0) {
      showNotification({
        type: 'error',
        title: 'Invalid Quantity',
        message: 'Please enter a valid positive quantity.',
        duration: 5000
      });
      return;
    }

    if (isNaN(priceNum) || priceNum <= 0) {
      showNotification({
        type: 'error',
        title: 'Invalid Price',
        message: 'Please enter a valid positive price.',
        duration: 5000
      });
      return;
    }

    // Check if deal with same quantity already exists
    if (discountDeals.some(deal => deal.quantity === quantityNum)) {
      showNotification({
        type: 'error',
        title: 'Duplicate Deal',
        message: 'A discount deal with this quantity already exists.',
        duration: 5000
      });
      return;
    }

    // Validate that deal price is less than regular price * quantity
    const regularPrice = parseFloat(salePrice);
    if (priceNum >= regularPrice * quantityNum) {
      showNotification({
        type: 'error',
        title: 'Invalid Deal Price',
        message: 'Deal price should be less than the regular price for the same quantity.',
        duration: 5000
      });
      return;
    }

    const newDeal = {
      id: Date.now(), // Simple ID for local state management
      quantity: quantityNum,
      price: priceNum
    };

    const updatedDeals = [...discountDeals, newDeal];
    logger.debug('[EditInventoryItem] Adding new deal:', newDeal);
    logger.debug('[EditInventoryItem] Updated deals array:', updatedDeals);

    setDiscountDeals(updatedDeals);
    setDealQuantity('');
    setDealPrice('');

    showNotification({
      type: 'success',
      title: 'Deal Added',
      message: `Discount deal added: ${quantityNum} items for $${priceNum.toFixed(2)}`,
      duration: 3000
    });
  };

  const removeDiscountDeal = (dealId) => {
    const dealToRemove = discountDeals.find(deal => deal.id === dealId);
    setDiscountDeals(discountDeals.filter(deal => deal.id !== dealId));

    if (dealToRemove) {
      showNotification({
        type: 'info',
        title: 'Deal Removed',
        message: `Discount deal removed: ${dealToRemove.quantity} items for $${dealToRemove.price.toFixed(2)}`,
        duration: 3000
      });
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProductDropdown && !event.target.closest('.product-search-container')) {
        setShowProductDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProductDropdown]);

  useEffect(() => {
    const fetchDropdownDataAndItem = async () => {
      setPageLoading(true);
      try {
        // Fetch supermarkets
        const supermarketsSnapshot = await getDocs(collection(db, 'Supermarkets'));
        let supermarketsList = supermarketsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (isEmployeeLimited) {
          supermarketsList = supermarketsList.filter(s => s.id === employeeSupermarketId);
        }
        setSupermarkets(supermarketsList);

        // Fetch products
        const productsSnapshot = await getDocs(collection(db, 'Products'));
        const productsList = productsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(productsList);

        // Fetch the inventory item
        if (!itemId) {
          showNotification({
            type: 'error',
            title: 'Missing Item ID',
            message: 'Inventory item ID is missing.',
            duration: 5000
          });
          setPageLoading(false);
          return;
        }
        const itemDoc = await getDoc(doc(db, 'Inventory', itemId));
        if (itemDoc.exists()) {
          const itemData = itemDoc.data();

          // Check if user has permission to edit this item
          if (isEmployeeLimited && itemData.supermarket_id !== employeeSupermarketId) {
            showNotification({
              type: 'error',
              title: 'Access Denied',
              message: "You don't have permission to edit this inventory item.",
              duration: 5000
            });
            setPageLoading(false);
            return;
          }

          setSelectedSupermarketId(itemData.supermarket_id || '');
          setSelectedProduct(itemData.product_id || '');
          setSalePrice((itemData.sale_price || 0).toString());
          const itemTaxCategory = itemData.tax_category || DEFAULT_TAX_CATEGORY;
          setTaxCategory(itemTaxCategory);
          setCustomTaxRate(itemTaxCategory === 'custom' && itemData.tax_rate != null ? (itemData.tax_rate * 100).toString() : '');
          setQuantity((itemData.quantity || 0).toString());

          // Load existing discount deals
          logger.debug('[EditInventoryItem] Loading item data:', itemData);
          logger.debug('[EditInventoryItem] Raw discount_deals from DB:', itemData.discount_deals);

          if (itemData.discount_deals && Array.isArray(itemData.discount_deals)) {
            const dealsWithIds = itemData.discount_deals.map((deal, index) => ({
              id: Date.now() + index, // Generate unique IDs for existing deals
              quantity: deal.quantity,
              price: deal.price
            }));
            logger.debug('[EditInventoryItem] Processed discount deals:', dealsWithIds);
            setDiscountDeals(dealsWithIds);
          } else {
            logger.debug('[EditInventoryItem] No discount deals found or not an array');
            setDiscountDeals([]);
          }

          // Set product search term and data for display
          const productData = productsList.find(p => p.id === itemData.product_id);
          if (productData) {
            setProductSearchTerm(productData.display_name || '');
            setSelectedProductData(productData);
          }
        } else {
          showNotification({
            type: 'error',
            title: 'Item Not Found',
            message: 'Inventory item not found.',
            duration: 5000
          });
        }

      } catch (err) {
        showNotification({
          type: 'error',
          title: 'Fetch Error',
          message: `Failed to fetch data. ${  err.message}`,
          duration: 5000
        });
        logger.error(err);
      }
      setPageLoading(false);
    };

    if (currentUser) {
      fetchDropdownDataAndItem();
    }
  }, [currentUser, userRole, employeeSupermarketId, isEmployeeLimited, itemId, showNotification]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedSupermarketId || !selectedProduct || !salePrice || !quantity) {
      showNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Please fill in all fields.',
        duration: 5000
      });
      return;
    }

    if (userRole === 'Employee' && !employeeSupermarketId) {
      showNotification({
        type: 'error',
        title: 'Access Error',
        message: "Cannot edit inventory: You are not assigned to a supermarket.",
        duration: 5000
      });
      return;
    }

    if (isEmployeeLimited && selectedSupermarketId !== employeeSupermarketId) {
      showNotification({
        type: 'error',
        title: 'Permission Error',
        message: "Error: You can only edit inventory for your assigned supermarket.",
        duration: 5000
      });
      return;
    }

    setSubmitting(true);
    try {
      const dealsToSave = discountDeals.length > 0 ? discountDeals.map(deal => ({
        quantity: deal.quantity,
        price: deal.price
      })) : [];

      logger.debug('[EditInventoryItem] Saving discount deals:', dealsToSave);
      logger.debug('[EditInventoryItem] Current discountDeals state:', discountDeals);

      let taxRate = getDefaultTaxRateForCategory(taxCategory);
      if (taxCategory === 'custom') {
        const customTaxRateNum = parseFloat(customTaxRate);
        if (isNaN(customTaxRateNum) || customTaxRateNum < 0 || customTaxRateNum > 100) {
          throw new Error('Custom GCT rate must be a number from 0 to 100.');
        }
        taxRate = customTaxRateNum / 100;
      }

      await updateDoc(doc(db, 'Inventory', itemId), {
        supermarket_id: selectedSupermarketId,
        product_id: selectedProduct,
        product_name: selectedProductData?.display_name || 'Unknown Product',
        product_upc: selectedProductData?.upc || null,
        sale_price: parseFloat(salePrice),
        price_tax_mode: selectedPriceTaxMode,
        sale_price_includes_tax: isTaxInclusivePricing,
        tax_category: taxCategory,
        tax_rate: taxRate,
        quantity: parseInt(quantity),
        discount_deals: dealsToSave,
        updated_by_uid: currentUser?.uid,
        updated_by_email: currentUser?.email || 'Unknown User',
        updated_at: new Date(),
      });

      showNotification({
        type: 'success',
        title: 'Update Successful',
        message: `Inventory item updated successfully! ${dealsToSave.length} discount deal(s) saved.`,
        duration: 5000
      });

      // Refresh the data to ensure deals are properly loaded
      logger.debug('[EditInventoryItem] Refreshing data after successful update...');
      const refreshedItemDoc = await getDoc(doc(db, 'Inventory', itemId));
      if (refreshedItemDoc.exists()) {
        const refreshedItemData = refreshedItemDoc.data();
        logger.debug('[EditInventoryItem] Refreshed item data:', refreshedItemData);
        logger.debug('[EditInventoryItem] Refreshed discount_deals:', refreshedItemData.discount_deals);
      }
    } catch (err) {
      showNotification({
        type: 'error',
        title: 'Update Failed',
        message: `Failed to update inventory item. ${  err.message}`,
        duration: 5000
      });
      logger.error(err);
    }
    setSubmitting(false);
  };

  const handleGoBack = () => navigate(-1);

  if (pageLoading && !currentUser) {
    return (
      <Layout>
        <Container className="px-3">
          <p>Loading user data...</p>
        </Container>
      </Layout>
    );
  }

  if (userRole === 'Employee' && !employeeSupermarketId && !pageLoading) {
    return (
      <Layout>
        <Container className="mt-3 px-3">
          <Button variant="outline-secondary" onClick={handleGoBack} className="mb-3">&larr; Go Back</Button>
          <div className="alert alert-danger" role="alert">
            <strong>Access Restricted</strong><br />
            You are not assigned to any supermarket. Please contact an administrator to edit inventory.
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container className="px-3">
        <Button variant="outline-secondary" onClick={handleGoBack} className="mt-3 mb-3">
          &larr; Go Back
        </Button>
        <h1 className="mb-3 text-break">Edit Inventory Item</h1>

        {pageLoading ? (
          <div className="text-center">
            <Spinner animation="border" className="mb-3" />
            <p>Loading form data...</p>
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="supermarketSelect" className="mb-3">
              <Form.Label>Supermarket</Form.Label>
              <Form.Control
                as="select"
                value={selectedSupermarketId}
                onChange={(e) => setSelectedSupermarketId(e.target.value)}
                required
                disabled={isEmployeeLimited || submitting}
                className="w-100"
                style={{ fontSize: '16px' }}
              >
                {supermarkets.map((supermarket) => (
                  <option key={supermarket.id} value={supermarket.id}>
                    {supermarket.display_name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="productSelect" className="mb-3">
              <Form.Label>Product</Form.Label>
              <div className="position-relative product-search-container">
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    placeholder="Search by product name, UPC, or description..."
                    value={productSearchTerm}
                    onChange={handleProductSearchChange}
                    onFocus={() => setShowProductDropdown(true)}
                    required
                    disabled={submitting || !canEdit}
                    className="w-100"
                    style={{ fontSize: '16px' }} // Prevents zoom on iOS
                  />
                  {selectedProductData && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        setSelectedProductData(null);
                        setSelectedProduct('');
                        setProductSearchTerm('');
                      }}
                      style={{
                        fontSize: '16px',
                        minWidth: '44px',
                        position: 'absolute',
                        right: '0',
                        top: '0',
                        height: '100%',
                        borderTopLeftRadius: '0',
                        borderBottomLeftRadius: '0',
                        zIndex: 10
                      }}
                    >
                      ×
                    </Button>
                  )}
                </div>

                {showProductDropdown && (
                  <div
                    className="position-absolute w-100 bg-white border rounded-bottom shadow-sm"
                    style={{
                      zIndex: 1000,
                      maxHeight: '300px',
                      overflowY: 'auto',
                      top: '100%',
                      maxWidth: '100vw'
                    }}
                  >
                    {filteredProducts.length > 0 ? (
                      <ListGroup variant="flush">
                        {filteredProducts.map((product) => (
                          <ListGroup.Item
                            key={product.id}
                            action
                            onClick={() => handleProductSelect(product)}
                            className="cursor-pointer p-3"
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2">
                              <div className="flex-grow-1 w-100">
                                <div className="fw-semibold text-break fs-6">
                                  {product.display_name}
                                </div>
                                {product.upc && (
                                  <div className="small text-muted mt-1">
                                    UPC: {product.upc}
                                  </div>
                                )}
                                {product.description && (
                                  <div className="small text-muted text-break mt-1">
                                    {product.description.length > 50
                                      ? `${product.description.substring(0, 50)}...`
                                      : product.description
                                    }
                                  </div>
                                )}
                              </div>
                              <div className="d-flex flex-wrap align-items-start gap-1">
                                {product.category && (
                                  <Badge bg="secondary" className="flex-shrink-0 small">
                                    {product.category}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : (
                      <div className="p-4 text-center text-muted">
                        <div className="small mb-2">
                          {productSearchTerm.trim()
                            ? 'No products found'
                            : 'Start typing to search products'
                          }
                        </div>
                      </div>
                    )}
                    {selectedProductData && (
                      <div className="border-top p-3">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            setSelectedProductData(null);
                            setSelectedProduct('');
                            setProductSearchTerm('');
                            setShowProductDropdown(false);
                          }}
                          className="w-100"
                        >
                          Cancel Selection
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {selectedProductData && (
                <div className="mt-2 p-3 bg-light rounded border">
                  <div className="d-flex flex-column gap-2">
                    <div>
                      <small className="text-muted">Selected: </small>
                      <span className="fw-semibold text-break">{selectedProductData.display_name}</span>
                    </div>
                    {selectedProductData.description && (
                      <div className="small text-muted text-break">
                        {selectedProductData.description}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Form.Group>

            <Form.Group controlId="salePrice" className="mb-3">
              <Form.Label>Sale Price ({isTaxInclusivePricing ? 'tax-inclusive where applicable' : 'before GCT'})</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                placeholder={isTaxInclusivePricing ? 'Enter shelf price including GCT where applicable' : 'Enter sale price before GCT'}
                value={salePrice}
                onChange={(e) => setSalePrice(e.target.value)}
                required
                disabled={submitting || !canEdit}
                className="w-100"
                style={{ fontSize: '16px' }} // Prevents zoom on iOS
              />
              <Form.Text className="text-muted small">
                Staff enter one price. This supermarket is configured for {selectedPriceTaxMode === DEFAULT_PRICE_TAX_MODE ? 'tax-inclusive' : 'tax-exclusive'} pricing.
              </Form.Text>
            </Form.Group>

            <Row>
              <Col md={taxCategory === 'custom' ? 6 : 12}>
                <Form.Group controlId="taxCategory" className="mb-3">
                  <Form.Label>Tax Category</Form.Label>
                  <Form.Select
                    value={taxCategory}
                    onChange={(e) => setTaxCategory(e.target.value)}
                    required
                    disabled={submitting || !canEdit}
                    className="w-100"
                    style={{ fontSize: '16px' }}
                  >
                    {TAX_CATEGORIES.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted small">
                    Select the Jamaica GCT treatment for this product. Use custom only when a product-specific rate is needed.
                  </Form.Text>
                </Form.Group>
              </Col>
              {taxCategory === 'custom' && (
                <Col md={6}>
                  <Form.Group controlId="customTaxRate" className="mb-3">
                    <Form.Label>Custom GCT Rate (%)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="Enter custom GCT rate"
                      value={customTaxRate}
                      onChange={(e) => setCustomTaxRate(e.target.value)}
                      required
                      disabled={submitting || !canEdit}
                      className="w-100"
                      style={{ fontSize: '16px' }}
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>

            <Form.Group controlId="quantity" className="mb-3">
              <Form.Label>Quantity</Form.Label>
              <Form.Control
                type="number"
                step="1"
                placeholder="Enter quantity"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                required
                disabled={submitting || !canEdit}
                className="w-100"
                style={{ fontSize: '16px' }} // Prevents zoom on iOS
              />
            </Form.Group>

            {/* Discount Deals Section */}
            <Form.Group controlId="discountDeals" className="mb-3">
              <Form.Label>Discount Deals (Optional)</Form.Label>
              <small className="text-muted d-block mb-2">
                Create bulk purchase discounts using the same {isTaxInclusivePricing ? 'tax-inclusive' : 'tax-exclusive'} pricing mode. For example: Buy 3 items for $8.00 instead of $12.00
              </small>
              <Card className="mb-3">
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Control
                        type="number"
                        step="1"
                        placeholder="Quantity for deal"
                        value={dealQuantity}
                        onChange={(e) => setDealQuantity(e.target.value)}
                        disabled={submitting || !canEdit}
                        className="w-100"
                        style={{ fontSize: '16px' }}
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Control
                        type="number"
                        step="0.01"
                        placeholder="Price for deal"
                        value={dealPrice}
                        onChange={(e) => setDealPrice(e.target.value)}
                        disabled={submitting || !canEdit}
                        className="w-100"
                        style={{ fontSize: '16px' }}
                      />
                    </Col>
                  </Row>
                  <Button
                    variant="outline-primary"
                    onClick={addDiscountDeal}
                    className="mt-2"
                    disabled={!dealQuantity || !dealPrice || submitting || !canEdit}
                  >
                    Add Deal
                  </Button>
                </Card.Body>
              </Card>

              {/* Display existing deals */}
              {discountDeals.length > 0 && (
                <div className="mb-3">
                  <h6>Current Discount Deals:</h6>
                  <ListGroup>
                    {discountDeals.map((deal) => {
                      const regularPrice = parseFloat(salePrice) * deal.quantity;
                      const savings = regularPrice - deal.price;
                      return (
                        <ListGroup.Item key={deal.id} className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{deal.quantity} items</strong> for <strong>${deal.price.toFixed(2)}</strong>
                            <br />
                            <small className="text-success">
                              Save ${savings.toFixed(2)} (${(deal.price / deal.quantity).toFixed(2)} per item)
                            </small>
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeDiscountDeal(deal.id)}
                            disabled={submitting || !canEdit}
                          >
                            Remove
                          </Button>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                </div>
              )}
            </Form.Group>

            <Button
              type="submit"
              variant="primary"
              className="w-100 mt-4 mb-5 py-3"
              disabled={submitting || !canEdit || (userRole === 'Employee' && !employeeSupermarketId)}
              style={{ fontSize: '16px' }}
            >
              {submitting ? (
                <div className="d-flex align-items-center justify-content-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Updating...
                </div>
              ) : (
                'Update Inventory Item'
              )}
            </Button>
          </Form>
        )}
      </Container>
    </Layout>
  );
};

export default EditInventoryItem;
